import {
  bigint,
  doublePrecision,
  integer,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from "drizzle-orm/pg-core"
import { assets } from "./asset"
import { contacts, locations } from "./contact"
import { estates } from "./estate"

// Purpose: Document status enum
export const documentStatusEnum = pgEnum("document_status_enum", ["PENDING", "UPLOADED"])

// Purpose: Lifecycle of the asynchronous AI document-type classification.
export const documentClassificationStatusEnum = pgEnum("document_classification_status_enum", [
  "PENDING",
  "COMPLETED",
  "FAILED",
])

// Purpose: Document type enum
export const documentTypeEnum = pgEnum("document_type_enum", [
  "BANK_STATEMENT",
  "COMPANY_BYLAWS",
  "COMPANY_FINANCIAL_STATEMENT",
  "COMPANY_INCORPORATION",
  "COMPANY_PARTNERSHIP_AGREEMENT",
  "COMPANY_SHAREHOLDER_AGREEMENT",
  "COMPANY_TAX",
  "COMPANY_WILL",
  "DEED",
  "ESTATE_PLAN",
  "FINANCIAL_PLAN",
  "FUNERAL_PLAN",
  "INSURANCE_POLICY",
  "INVESTMENT_STATEMENT",
  "LAND_TITLE",
  "LAST_WILL_AND_TESTAMENT",
  "LOAN_DOCUMENT",
  "MEDICAL_DIRECTIVE",
  "MORTGAGE_DOCUMENT",
  "OTHER",
  "PERSONAL_TAX",
  "POWER_OF_ATTORNEY",
  "PROPERTY_ASSESSMENT",
  "PROPERTY_RECORD",
  "RETIREMENT_PLAN",
  "SERVICE_AGREEMENT",
  "TAX_PLAN",
  "TRUST",
  "TRUST_TAXES",
  "UNCLASSIFIED",
])

// Purpose: Defines categories for documents.
export const documentTypes = pgTable("document_type", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 100 }).notNull().unique(),
})

// Purpose: Define current status for documents, eg pending, uploaded etc
export const documentStatuses = pgTable("document_status", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 50 }).notNull().unique(),
})

// Purpose: Stores metadata about documents related to an estate. Actual files are stored in cloud storage.
export const documents = pgTable("document", {
  id: uuid("id").primaryKey().defaultRandom(),
  estateId: uuid("estate_id")
    .notNull()
    .references(() => estates.id, { onDelete: "cascade" }),
  type: documentTypeEnum("type").notNull(),
  status: documentStatusEnum("status").notNull(),
  assetId: uuid("asset_id").references(() => assets.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
  locationId: uuid("location_id").references(() => locations.id, { onDelete: "set null" }),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  filePath: varchar("file_path", { length: 512 }).notNull(),
  uploadDate: timestamp("upload_date").notNull(),
  lastModifiedDate: timestamp("last_modified_date"),
  fileSizeBytes: bigint("file_size_bytes", { mode: "number" }),
  mimeType: varchar("mime_type", { length: 100 }),
  processingStartedAt: timestamp("processing_started_at"),
  // AI document-type classification result (populated asynchronously; see the
  // classify branch of the document-processing state machine).
  classificationStatus: documentClassificationStatusEnum("classification_status"),
  classifiedType: documentTypeEnum("classified_type"),
  classificationConfidence: doublePrecision("classification_confidence"),
  classificationReasoning: text("classification_reasoning"),
  classificationPagesUsed: integer("classification_pages_used"),
  classificationCompletedAt: timestamp("classification_completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})
